import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

const FAVORITES_KEY = 'faind_favorites'

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<any>(null)
  const pendingOperations = useRef<Set<string>>(new Set())

  // Initialize: Load from localStorage or Supabase
  useEffect(() => {
    initializeFavorites()

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔐 Auth state changed:', event, session?.user?.email)
        setUser(session?.user ?? null)

        if (event === 'SIGNED_IN' && session?.user) {
          // User just signed in - migrate localStorage to Supabase
          await migrateLocalStorageToSupabase(session.user.id)
        } else if (event === 'SIGNED_OUT') {
          // User signed out - load from localStorage
          loadFromLocalStorage()
        }
      },
    )

    return () => {
      authListener.subscription.unsubscribe()
    }
  }, [])

  const initializeFavorites = async () => {
    setLoading(true)

    // Check if user is logged in
    const {
      data: { session },
    } = await supabase.auth.getSession()

    console.log('👤 Current session:', session?.user?.email || 'Not logged in')
    setUser(session?.user ?? null)

    if (session?.user) {
      // Load from Supabase
      await loadFromSupabase(session.user.id)
    } else {
      // Load from localStorage
      loadFromLocalStorage()
    }

    setLoading(false)
  }

  const loadFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem(FAVORITES_KEY)
      const parsed = stored ? JSON.parse(stored) : []
      console.log('💾 Loaded from localStorage:', parsed.length, 'favorites')
      setFavorites(parsed)
    } catch (error) {
      console.error('❌ Error loading favorites from localStorage:', error)
      setFavorites([])
    }
  }

  const loadFromSupabase = async (userId: string) => {
    try {
      console.log('🔄 Loading favorites from Supabase for user:', userId)

      const { data, error } = await supabase
        .from('user_favorites')
        .select('tool_id')
        .eq('user_id', userId)

      if (error) {
        console.error('❌ Supabase load error:', error)
        throw error
      }

      const toolIds = data?.map((f) => f.tool_id) || []
      console.log(
        '✅ Loaded from Supabase:',
        toolIds.length,
        'favorites',
        toolIds,
      )
      setFavorites(toolIds)
    } catch (error) {
      console.error('❌ Error loading favorites from Supabase:', error)
      // Fallback to localStorage
      loadFromLocalStorage()
    }
  }

  const migrateLocalStorageToSupabase = async (userId: string) => {
    try {
      // Get favorites from localStorage
      const stored = localStorage.getItem(FAVORITES_KEY)
      const localFavorites: string[] = stored ? JSON.parse(stored) : []

      if (localFavorites.length === 0) {
        console.log('📦 No local favorites to migrate')
        return
      }

      console.log(
        '🚀 Migrating',
        localFavorites.length,
        'favorites to Supabase',
      )

      // Insert into Supabase (ignore duplicates)
      const favoritesToInsert = localFavorites.map((toolId) => ({
        user_id: userId,
        tool_id: toolId,
      }))

      const { error } = await supabase
        .from('user_favorites')
        .upsert(favoritesToInsert, { onConflict: 'user_id,tool_id' })

      if (error) throw error

      // Clear localStorage after successful migration
      localStorage.removeItem(FAVORITES_KEY)

      // Reload from Supabase
      await loadFromSupabase(userId)

      console.log('✅ Favorites migrated to Supabase')
    } catch (error) {
      console.error('❌ Error migrating favorites:', error)
    }
  }

  const toggleFavorite = async (toolId: string) => {
    // Prevent duplicate operations
    if (pendingOperations.current.has(toolId)) {
      console.log('⏳ Operation already in progress for:', toolId)
      return
    }

    console.log(
      '❤️ Toggle favorite:',
      toolId,
      'User:',
      user?.email || 'Not logged in',
    )

    // Mark as pending
    pendingOperations.current.add(toolId)

    const isFavorited = favorites.includes(toolId)
    const newFavorites = isFavorited
      ? favorites.filter((id) => id !== toolId)
      : [...favorites, toolId]

    console.log(
      '📝 New favorites:',
      isFavorited ? 'Removing' : 'Adding',
      toolId,
    )

    // Optimistic update
    setFavorites(newFavorites)

    if (user) {
      // Save to Supabase
      try {
        if (isFavorited) {
          // Remove favorite
          console.log('🗑️ Deleting from Supabase...', {
            user_id: user.id,
            tool_id: toolId,
          })

          const { data, error } = await supabase
            .from('user_favorites')
            .delete()
            .eq('user_id', user.id)
            .eq('tool_id', toolId)
            .select()

          console.log('🔍 Delete response:', { data, error })

          if (error) {
            console.error('❌ Delete error:', error)
            throw error
          }
          console.log('✅ Deleted from Supabase')
        } else {
          // Add favorite
          console.log('➕ Adding to Supabase...', {
            user_id: user.id,
            tool_id: toolId,
          })

          const { data, error } = await supabase
            .from('user_favorites')
            .insert({ user_id: user.id, tool_id: toolId })
            .select()

          console.log('🔍 Insert response:', { data, error })

          if (error) {
            console.error('❌ Insert error:', error)
            console.error('❌ Error code:', error.code)
            console.error('❌ Error message:', error.message)
            console.error('❌ Error details:', error.details)
            console.error('❌ Error hint:', error.hint)
            throw error
          }

          if (!data || data.length === 0) {
            console.error(
              '❌ Insert returned no data - possible RLS policy issue',
            )
            throw new Error(
              'Insert succeeded but returned no data. Check RLS policies.',
            )
          }

          console.log('✅ Added to Supabase:', data)
        }
      } catch (error: any) {
        console.error('❌ Error updating favorite in Supabase:', error)
        console.error('❌ Full error object:', JSON.stringify(error, null, 2))

        // Revert on error
        setFavorites(favorites)

        // Show detailed error message
        let errorMessage = 'Failed to save favorite. '

        if (error.code === '42P01') {
          errorMessage +=
            'The user_favorites table does not exist. Please run the SQL setup.'
        } else if (error.code === '42501') {
          errorMessage += 'Permission denied. Please check RLS policies.'
        } else if (error.message?.includes('RLS')) {
          errorMessage +=
            'Row Level Security is blocking this operation. Please run the SQL setup.'
        } else {
          errorMessage += error.message || 'Unknown error occurred.'
        }

        alert(errorMessage)
      } finally {
        // Remove from pending
        pendingOperations.current.delete(toolId)
      }
    } else {
      // Save to localStorage
      try {
        console.log('💾 Saving to localStorage...')
        localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites))
        console.log('✅ Saved to localStorage')
      } catch (error) {
        console.error('❌ Error saving to localStorage:', error)
      } finally {
        // Remove from pending
        pendingOperations.current.delete(toolId)
      }
    }
  }

  const isFavorite = (toolId: string) => favorites.includes(toolId)

  return {
    favorites,
    loading,
    user,
    toggleFavorite,
    isFavorite,
  }
}
